import { Badge, Button } from "@/components/ui";
import type {
  AiChatGuardianDecision,
  AiChatGuardianReview,
} from "@/lib/types/ai-chat";
import { cn } from "@/lib/utils/cn";
import { AlertTriangle, Check, LoaderCircle, Wrench, X } from "lucide-react";
import { useState } from "react";
import { ChatApprovalAction } from "./ChatApprovalAction";

export type ChatApprovalCardReview = Pick<
  AiChatGuardianReview,
  "id" | "runId" | "status"
> &
  Partial<
    Pick<
      AiChatGuardianReview,
      | "commandId"
      | "action"
      | "actionRequiresDetail"
      | "approvalQuestion"
      | "approvalRequired"
      | "reason"
      | "riskLevel"
    >
  >;

export type ChatApprovalCardProps = {
  detail?: AiChatGuardianReview;
  isApprovalLoading?: boolean;
  isDecidingApproval?: boolean;
  onDecideApproval?: (
    runId: string,
    reviewId: string,
    decision: AiChatGuardianDecision,
  ) => Promise<void> | void;
  onLoadApprovalDetail?: (
    runId: string,
    reviewId: string,
  ) => Promise<void> | void;
  review: ChatApprovalCardReview;
};

function readableLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character: string): string => character.toUpperCase());
}

export function ChatApprovalCard({
  detail,
  isApprovalLoading,
  isDecidingApproval,
  onDecideApproval,
  onLoadApprovalDetail,
  review,
}: ChatApprovalCardProps) {
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const displayedReview = detail ?? review;
  const isWaiting = displayedReview.status === "awaiting_human";
  const question =
    displayedReview.approvalQuestion ?? "Review this LawWorks action.";
  const canLoad = Boolean(!detail && onLoadApprovalDetail);
  const canDecide = Boolean(isWaiting && detail && onDecideApproval);
  const detailIsLoading = isApprovalLoading || isLoadingDetail;
  const decisionIsLoading = isDecidingApproval || isSubmitting;

  const handleDecision = async (decision: AiChatGuardianDecision) => {
    if (!onDecideApproval) return;
    setError(undefined);
    setIsSubmitting(true);
    try {
      await onDecideApproval(review.runId, review.id, decision);
    } catch {
      setError("The decision couldn't be saved. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoadDetail = async () => {
    if (!onLoadApprovalDetail) return;
    setError(undefined);
    setIsLoadingDetail(true);
    try {
      await onLoadApprovalDetail(review.runId, review.id);
    } catch {
      setError("The exact action couldn't be loaded. Try again.");
    } finally {
      setIsLoadingDetail(false);
    }
  };

  return (
    <section
      className={cn(
        "rounded-xl border px-4 py-3.5",
        isWaiting
          ? "border-amber-300/70 bg-amber-50/50 dark:border-amber-800/70 dark:bg-amber-950/15"
          : "border-border/70 bg-muted/20",
      )}
    >
      <div className="flex items-start gap-3">
        {isWaiting ? (
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        ) : (
          <Wrench className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-foreground font-medium">
              {isWaiting ? "Your review is needed" : "Action review"}
            </p>
            <Badge variant={isWaiting ? "amber" : "muted"}>
              {readableLabel(displayedReview.status)}
            </Badge>
          </div>
          <p
            data-chat-search-content
            className="text-foreground mt-1.5 text-sm leading-6"
          >
            {question}
          </p>
          {detail ? (
            <div className="border-border/60 mt-3 border-t pt-3">
              {detail.reason ? (
                <p className="text-muted-foreground mb-3 text-sm">
                  {detail.reason}
                </p>
              ) : null}
              {detail.action ? (
                <ChatApprovalAction
                  action={detail.action}
                  commandId={detail.commandId}
                />
              ) : null}
            </div>
          ) : review.action && !review.actionRequiresDetail ? (
            <div className="border-border/60 mt-3 border-t pt-3">
              <ChatApprovalAction
                action={review.action}
                commandId={review.commandId}
              />
            </div>
          ) : null}
          {error ? (
            <p role="alert" className="text-destructive mt-3 text-sm">
              {error}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            {!detail && canLoad ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={detailIsLoading}
                onClick={() => {
                  void handleLoadDetail();
                }}
              >
                {detailIsLoading && <LoaderCircle className="animate-spin" />}
                Review exact action
              </Button>
            ) : null}
            {canDecide ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  disabled={decisionIsLoading}
                  onClick={() => {
                    void handleDecision("approve");
                  }}
                >
                  {decisionIsLoading ? (
                    <LoaderCircle className="animate-spin" />
                  ) : (
                    <Check />
                  )}
                  Approve
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={decisionIsLoading}
                  onClick={() => {
                    void handleDecision("reject");
                  }}
                >
                  <X /> Reject
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
